import React, { useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { ChevronLeftIcon, ChevronRightIcon, Maximize2Icon } from 'lucide-react';
import { samplePages } from '../../data/samplePages';
export function PagePreview() {
  const [index, setIndex] = useState(0);
  const reduce = useReducedMotion();
  if (samplePages.length === 0) return null;
  const page = samplePages[index];
  const go = (dir: number) => setIndex(i => (i + dir + samplePages.length) % samplePages.length);
  const pad = (n: number) => String(n).padStart(2, '0');
  return <section aria-labelledby="preview-heading" className="bg-onyx" data-id="element-141" data-mp="3h1qfxfgqbq5l">
      <div className="mx-auto max-w-5xl px-5 py-16 md:py-24" data-id="element-142" data-mp="3gzw3kpjix6d4">
        <div className="mx-auto max-w-3xl text-center" data-id="element-143" data-mp="3c472uivrpjf8">
          <h2 id="preview-heading" className="font-display text-5xl font-extrabold uppercase leading-[0.92] text-white md:text-6xl" data-id="element-144" data-mp="1mnxssj5kg2f4">
            Take a look <span className="text-gold" data-id="element-145" data-mp="1hr7h080snszi">inside the material</span>
          </h2>
          <p className="mt-5 text-lg leading-relaxed text-white/75" data-id="element-146" data-mp="241hadqk6inh9">
            Real pages from the plan — calendar, station cards and complete sessions. Tap any page to enlarge it.
          </p>
        </div>

        <div className="mt-12 overflow-hidden rounded-xl border border-white/10 bg-card" data-id="element-147" data-mp="2cy83bu3fpm7m">
          <div className="relative flex items-center justify-center bg-white/[0.03] p-4 sm:p-8" data-id="element-148" data-mp="2hscaoegm3w98">
            <AnimatePresence mode="wait" initial={false} data-id="element-149" data-mp="2mpw0mbmo9xh0">
              <motion.a key={index} href={page.image} target="_blank" rel="noopener noreferrer" initial={reduce ? {
              opacity: 0
            } : {
              opacity: 0,
              x: 16
            }} animate={{
              opacity: 1,
              x: 0
            }} exit={reduce ? {
              opacity: 0
            } : {
              opacity: 0,
              x: -16
            }} transition={{
              duration: 0.2,
              ease: [0.23, 1, 0.32, 1]
            }} className="group relative block" data-id="element-150" data-mp="2rofls9tfz58b">
                <img src={page.image} alt={page.title} loading="lazy" width={1622} height={2294} className="h-auto max-h-[70vh] w-auto max-w-full rounded-md object-contain shadow-[0_20px_40px_-16px_rgba(0,0,0,0.8)]" data-id="element-151" data-mp="29m9ypwexq9ht" />
                <span className="absolute right-3 top-3 flex items-center gap-1.5 rounded-md bg-onyx/80 px-2.5 py-1.5 text-xs font-semibold text-white" data-id="element-152" data-mp="2ekl51x60u1va">
                  <Maximize2Icon aria-hidden="true" className="h-3.5 w-3.5" data-id="element-153" data-mp="2nn2tmi95f6lg" />
                  Enlarge
                </span>
              </motion.a>
            </AnimatePresence>
          </div>
          <div className="flex gap-2 overflow-x-auto border-t border-white/10 px-5 py-4 sm:justify-center sm:px-8" data-id="element-154" data-mp="2xbug4avfwyoz">
            {samplePages.map((p, i) => <button key={p.image} type="button" onClick={() => setIndex(i)} aria-label={`Show page: ${p.title}`} aria-current={i === index} className={`w-14 shrink-0 overflow-hidden rounded border-2 transition-[border-color,opacity] duration-150 ease-out focus:outline-none focus-visible:ring-2 focus-visible:ring-gold sm:w-16 ${i === index ? 'border-gold opacity-100' : 'border-transparent opacity-60 hover:opacity-100'}`} data-id="element-155" data-mp="3ba2kv4ypx4ap" data-mpi={i}>
                <img src={p.image} alt="" loading="lazy" className="aspect-[1622/2294] w-full object-cover object-top" data-id="element-156" data-mp="377ky59syw7qj" />
              </button>)}
          </div>
          <div className="flex flex-col gap-5 border-t border-white/10 px-5 py-6 sm:flex-row sm:items-center sm:justify-between sm:px-8" data-id="element-157" data-mp="2xayayed654lc">
            <div aria-live="polite" data-id="element-158" data-mp="329jd4jbzdycf">
              <p className="text-sm font-bold tracking-[0.16em] text-gold" data-id="element-159" data-mp="2o9qpwxzny097">
                {pad(index + 1)} / {pad(samplePages.length)}
              </p>
              <h3 className="mt-1 font-display text-2xl font-extrabold uppercase text-white" data-id="element-160" data-mp="2t8223fdfkmul">{page.title}</h3>
              <p className="mt-1 max-w-md text-base text-white/75" data-id="element-161" data-mp="2l741j3xs67ir">{page.caption}</p>
            </div>
            <div className="flex shrink-0 gap-3" data-id="element-162" data-mp="2gavtsblwxglm">
              {[{
              dir: -1,
              label: 'Previous page',
              Icon: ChevronLeftIcon
            }, {
              dir: 1,
              label: 'Next page',
              Icon: ChevronRightIcon
            }].map(({
              dir,
              label,
              Icon
            }) => <button key={label} type="button" onClick={() => go(dir)} aria-label={label} className="flex h-12 w-12 items-center justify-center rounded-lg border border-white/20 text-white transition-colors duration-150 ease-out hover:border-gold hover:text-gold focus:outline-none focus-visible:ring-4 focus-visible:ring-gold/50" data-id="element-163" data-mp="22ifjcpqomgao">
                  <Icon aria-hidden="true" className="h-5 w-5" data-id="element-164" data-mp="3muwscpsidqhz" />
                </button>)}
            </div>
          </div>
        </div>
      </div>
    </section>;
}